import React, { useState, useEffect, useMemo } from "react";
import {
  findMemberInvitationTokenByEmail,
  findMemberInvitationTokenByMobile,
} from "@/services/token.service";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { isValidName } from "@/utils";
import { useRegister } from "@/hooks/useRegister";
import { useLocation, useNavigate } from "react-router-dom";
import { MButton } from "@/components/ui/atoms/MButtons";
import MInputField from "@/components/ui/atoms/MInputField";
import { useTranslation } from "react-i18next";
import { InvitationState, RegisterUserPayload } from "@/types/register";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import { DEFAULT_START_DATE } from "@/utils/constants";
import { genderOptions } from "@/types/member";
import { IconCaretDownFilled, IconUsersGroup, IconUser } from "@tabler/icons-react";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import MLoader from "@/components/ui/atoms/MLoader";
import { useRegisterContext } from "@/context/RegisterContext";
import MOnboardingHeader from "@/components/ui/organisms/MOnboardingHeader";
import MSelectCard from "@/components/ui/atoms/MSelectCard";
import { MDialog } from "@/components/ui/atoms/MDialog";
import { subscriptionService } from "@/services/subscription.service";
import MTopBar from "@/components/ui/atoms/MTopBar";


const SignupConfirm: React.FC = () => {
  const { t } = useTranslation();
  const { credentials, updateCredentials } = useRegisterContext();
  const [tokenData, setTokenData] = useState<any | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [showInviteOptions, setShowInviteOptions] = useState(false);
  const [inviteSelection, setInviteSelection] = useState<
    "join" | "create" | null
  >((credentials.inviteSelection as "join" | "create" | null) || null);
  const [joinFamilyConsent, setJoinFamilyConsent] = useState(false);
  const [showFamilyConsent, setShowFamilyConsent] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);

  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const location = useLocation();
  const invitationState = location.state as InvitationState | undefined;
  const navigate = useNavigate();
  const { email, mobile, dialCode, skipInviteOptions, inviteSelection: stateInviteSelection } = (location.state || {}) as {
    email?: string;
    mobile?: string;
    dialCode?: string;
    inviteToken?: string;
    skipInviteOptions?: boolean;
    inviteSelection?: "join" | "create";
  };
  const [firstName, setFirstName] = useState(invitationState?.firstName || "");
  const [lastName, setLastName] = useState(invitationState?.lastName || "");
  const [dob, setDob] = useState<string>(invitationState?.dob || "");
  const [gender, setGender] = useState<number | undefined>(
    invitationState?.gender
  );
  const [genderDrawerOpen, setGenderDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameIsValid = isValidName(firstName.trim()) && isValidName(lastName.trim());
  const [loading, setLoading] = useState(false);
  const { registerUser, createOwnFamily } = useRegister();

  // Load token on page load
  useEffect(() => {
    loadToken();
  }, [email, mobile, skipInviteOptions]);

  // Restore form inputs from context on mount
  useEffect(() => {
    if (!invitationState) {
      if (credentials.firstName) {
        setFirstName(credentials.firstName);
      }
      if (credentials.lastName) {
        setLastName(credentials.lastName);
      }
    }
  }, []);

  // Flush tour-related localStorage items on component mount
  useEffect(() => {
    localStorage.removeItem('tour_seen_app');
    localStorage.removeItem('tour_modal_count');
  }, []);

  // Save firstName to context whenever it changes
  useEffect(() => {
    if (firstName) {
      updateCredentials({ firstName });
    }
  }, [firstName]);

  // Save lastName to context whenever it changes
  useEffect(() => {
    if (lastName) {
      updateCredentials({ lastName });
    }
  }, [lastName]);

  // Save dob to context whenever it changes
  useEffect(() => {
    if (dob) {
      updateCredentials({ dob });
    }
  }, [dob]);

  // Save gender to context whenever it changes
  useEffect(() => {
    if (gender !== undefined) {
      updateCredentials({ gender });
    }
  }, [gender]);

  // Memoize date value to prevent MDatePicker from resetting
  const dateValue = useMemo(() => {
    if (dob && /^\d{4}-\d{2}$/.test(dob)) {
      return new Date(
        Number(dob.split("-")[0]),
        Number(dob.split("-")[1]) - 1
      );
    } else if (dob && /^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      return new Date(dob);
    }
    return undefined;
  }, [dob]);

  // Memoize maxDate to prevent unnecessary re-renders
  const maxDate = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear() - 18;
    const month = today.getMonth();
    return new Date(year, month + 1, 0);
  }, []);

  // Fetch trial plan ID
  const getTrialPlanId = async (): Promise<string | null> => {
    try {
      const plans = await subscriptionService.getPublicPlans();
      const trialPlan = plans.find(plan => plan.type === 'trial');
      return trialPlan?.id || null;
    } catch (error) {
      console.error('Error fetching plans:', error);
      return null;
    }
  };

  const loadToken = async () => {
    // If skipInviteOptions is true, don't show invite options, go straight to form
    if (skipInviteOptions) {
      setShowInviteOptions(false);
      setInviteSelection(stateInviteSelection || 'join');

      // If tokenData is passed in state, use it to populate form
      if (location.state && (location.state as any).tokenData) {
        const data = (location.state as any).tokenData;
        setTokenData(data);

        // Populate form fields from token data
        const tokenParams = data.token_params;
        if (tokenParams?.firstName) {
          setFirstName(tokenParams.firstName);
        }
        if (tokenParams?.lastName) {
          setLastName(tokenParams.lastName);
        }
        if (tokenParams?.dobYear && tokenParams?.dobMonth) {
          const date = new Date(
            tokenParams.dobYear,
            tokenParams.dobMonth - 1,
            1
          );
          setDob(
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
              2,
              "0"
            )}`
          );
        }
        if (tokenParams?.gender !== undefined) {
          setGender(tokenParams.gender);
        }
      }
      return;
    }

    // Check for token in state or query param
    let token: string | undefined = undefined;
    if (location.state && (location.state as any).inviteToken) {
      token = (location.state as any).inviteToken;
    } else {
      const params = new URLSearchParams(location.search);
      token = params.get("token") || undefined;
    }

    if (token) {
      // Token found in URL/state, show invite options
      setShowInviteOptions(true);
      return;
    }

    // Search for token by email or mobile
    if (!email && !mobile) {
      return;
    }

    setTokenLoading(true);
    try {
      const data = email
        ? await findMemberInvitationTokenByEmail(email)
        : await findMemberInvitationTokenByMobile(mobile);
      setTokenLoading(false);

      if (
        data &&
        typeof data.type === "string" &&
        data.type.toLowerCase() === "member_invitation" &&
        (data.token_params?.email === email ||
          data.token_params?.mobile === mobile)
      ) {
        setTokenData(data);
        setShowInviteOptions(true);
        // Populate form fields from token data
        const tokenParams = data.token_params;
        if (tokenParams?.firstName) {
          setFirstName(tokenParams.firstName);
        }
        if (tokenParams?.lastName) {
          setLastName(tokenParams.lastName);
        }
        if (tokenParams?.dobYear && tokenParams?.dobMonth) {
          const date = new Date(
            tokenParams.dobYear,
            tokenParams.dobMonth - 1,
            1
          );
          setDob(
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
              2,
              "0"
            )}`
          );
        }
        if (tokenParams?.gender) {
          setGender(tokenParams.gender);
        }
      }
    } catch (err: any) {
      setTokenError(err?.message || "No invitation found.");
      setTokenLoading(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    setLoading(true);
    if (!nameIsValid) {
      setError(t('auth.onboarding.signupConfirm.errorName'));
      setLoading(false);
      return;
    }

    // If token exists and user has made a selection, execute appropriate action
    if (tokenData && inviteSelection) {
      if (inviteSelection === "join") {
        await handleJoinFamily();
        setLoading(false);
        return;
      } else if (inviteSelection === "create") {
        // Create own family - register and go to success
        await handleCreateOwnFamily();
        setLoading(false);
        return;
      }
    }

    // Check for token in state or query param (for direct token flow)
    let token: string | undefined = undefined;
    if (location.state && (location.state as any).inviteToken) {
      token = (location.state as any).inviteToken;
    } else {
      const params = new URLSearchParams(location.search);
      token = params.get("token") || undefined;
    }

    if (token) {
      await registerAndNavigate(token);
      setLoading(false);
      return;
    }

    // No token, regular registration - register and go to success
    await registerAndNavigate();
    setLoading(false);
  };

  const registerAndNavigate = async (token?: string) => {
    try {
      const planId = await getTrialPlanId();
      if (!planId) {
        setError(t('auth.onboarding.signupConfirm.errorPlan'));
        return;
      }

      const payload: RegisterUserPayload = {
        first_name: firstName,
        last_name: lastName,
        dob_month: Number(dob.split("-")[1]),
        dob_year: Number(dob.split("-")[0]),
        gender,
        planId,
      };
      if (mobile) payload.mobile = mobile;
      if (dialCode) payload.dialCode = dialCode;
      if (email) payload.email = email;
      payload.other_attrs = {
        specialNeeds: false,
        docUploaded: false,
      };
      await registerUser(payload, token);

      // Fetch trial end date
      let trialEndDate = "";
      try {
        const subscription = await subscriptionService.getCurrentSubscription();
        trialEndDate = subscription.endDate;
      } catch (error) {
        console.error("Error fetching subscription:", error);
      }

      navigate("/register/success", {
        state: {
          firstName,
          trialEndDate,
          isJoiningFamily: false
        },
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || t('auth.onboarding.signupConfirm.errorRegistration')
      );
    }
  };

  const handleJoinFamily = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload: any = { first_name: firstName };
      if (mobile) payload.mobile = mobile;
      if (dialCode) payload.dialCode = dialCode;
      if (email) payload.email = email;
      await registerUser(payload, tokenData?.token);
      const inviterName = tokenData?.token_params?.inviteBy;
      // Go directly to success page (skip second invite-success)
      navigate("/register/success", {
        state: {
          firstName,
          inviterName,
          isJoiningFamily: true
        },
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || t('auth.onboarding.signupConfirm.errorRegistration')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOwnFamily = async () => {
    setLoading(true);
    setError(null);
    try {
      const planId = await getTrialPlanId();
      if (!planId) {
        setError(t('auth.onboarding.signupConfirm.errorPlan'));
        setLoading(false);
        return;
      }

      const payload: any = {
        first_name: firstName,
        last_name: lastName,
        dobMonth: Number(dob.split("-")[1]),
        dobYear: Number(dob.split("-")[0]),
        gender,
        planId,
      };
      if (mobile) payload.mobile = mobile;
      if (dialCode) payload.dialCode = dialCode;
      if (email) payload.email = email;
      await createOwnFamily(payload, tokenData?.token);

      // Fetch trial end date
      let trialEndDate = "";
      try {
        const subscription = await subscriptionService.getCurrentSubscription();
        trialEndDate = subscription.endDate;
      } catch (error) {
        console.error("Error fetching subscription:", error);
      }

      navigate("/register/success", {
        state: {
          firstName,
          trialEndDate,
          isJoiningFamily: false
        },
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || t('auth.onboarding.signupConfirm.errorRegistration')
      );
    } finally {
      setLoading(false);
    }
  };

  if (tokenLoading) return <MLoader />;

  const inviterName = tokenData?.token_params?.inviteBy;
  if (showInviteOptions) {
    return (
      <div className="w-screen min-h-screen bg-white relative overflow-hidden">
        <div className="absolute bottom-[-200px] left-[-138px] right-0 w-[684px] h-[684px] bg-[linear-gradient(0deg,rgba(90,65,153,0)_14.91%,rgba(149,108,255,0.2)_103.03%)] rounded-full"></div>

        <div className="relative z-10  pb-8">
          <MTopBar
            onBack={() => navigate(-1)}
            exitPath="/"
            exitKey="exit"
            withBackground={false}
            hideProfilePicker
          />

          {/* Title */}
          <p className="text-[16px] text-[#797979] mb-5 mt-[60px] text-center">{t('auth.onboarding.signupConfirm.inviteTitle')}</p>
          <h1 className="text-[28px] leading-[1.2] font-bold text-center mb-48">
            <span className="text-[#7046AE] text-[24px]">{inviterName}</span>
            <br />
            <span className="text-[#36116D] text-[24px]">{t('auth.onboarding.signupConfirm.inviteSubtitle1')}</span>
            <br />
            <span className="text-[#36116D] text-[24px]">{t('auth.onboarding.signupConfirm.inviteSubtitle2')}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-base text-[#8764BB] font-[600] mb-4 px-6 ">
            {t('auth.onboarding.signupConfirm.selectPrompt')}
          </p>

          <div className="space-y-4 mb-8 px-6">
            <MSelectCard
              className="px-[20px] py-[24px]"
              text={t('auth.onboarding.signupConfirm.joinFamily', { name: inviterName ? inviterName.split(" ")[0] : "" })}
              leftIcon={
                <IconUsersGroup />
              }
              selected={inviteSelection === "join"}
              variant="onboarding"
              onClick={() => {
                setInviteSelection("join");
                updateCredentials({ inviteSelection: "join" });
                // Restore pre-filled data from token for "join family"
                if (tokenData?.token_params) {
                  const tokenParams = tokenData.token_params;
                  if (tokenParams?.firstName) {
                    setFirstName(tokenParams.firstName);
                  }
                  if (tokenParams?.lastName) {
                    setLastName(tokenParams.lastName);
                  }
                  if (tokenParams?.dobYear && tokenParams?.dobMonth) {
                    const date = new Date(
                      tokenParams.dobYear,
                      tokenParams.dobMonth - 1,
                      1
                    );
                    setDob(
                      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
                    );
                  }
                  if (tokenParams?.gender !== undefined) {
                    setGender(tokenParams.gender);
                  }
                }
                setShowConsentModal(true);
              }}
            />

            {/* Set up own family Option */}
            <MSelectCard
              text={t('auth.onboarding.signupConfirm.setupOwnFamily')}
              leftIcon={
                <IconUser />
              }
              selected={inviteSelection === "create"}
              variant="onboarding"
              onClick={() => {
                setInviteSelection("create");
                updateCredentials({ inviteSelection: "create" });
                // Clear pre-filled fields for "create own family"
                setFirstName("");
                setLastName("");
                setDob("");
                setGender(undefined);
                setShowInviteOptions(false);
              }}
            />
          </div>
        </div>

        {/* Consent Modal */}
        <MDialog
          isOpen={showConsentModal}
          onClose={() => setShowConsentModal(false)}
          title={t('auth.onboarding.signupConfirm.consentModalTitle')}
          description={t('auth.onboarding.signupConfirm.consentModalDescription', { name: inviterName })}
          primaryCtaText={t('auth.onboarding.signupConfirm.consentModalConfirm')}
          onPrimaryCta={() => {
            setShowConsentModal(false);
            setShowInviteOptions(false);
          }}
          showCloseButton={true}
        />
      </div>
    );
  }

  return (
    <>
      <FooterDrawer
        open={showMoreInfo}
        onClose={() => setShowMoreInfo(false)}
        title={t("common.learnMore")}
        showClose
      >
        <div className="flex flex-col items-center justify-center px-6 py-8">
          <p className="font-medium mb-2" style={{ fontSize: 16 }}>
            {t("onboarding.step5.learnMoreContent1")}
            <br />
            <br />
            {t("onboarding.step5.learnMoreContent2")}
          </p>
        </div>
      </FooterDrawer>
      <div className="w-screen bg-white flex flex-col" style={{ height: '100dvh' }}>
        {/* Onboarding Header with gradient, back/exit, and step indicators */}
        <div className="auth-sticky-header">
          <MOnboardingHeader
            currentStep={3}
            totalSteps={3}
            onBack={() => tokenData ? setShowInviteOptions(true) : navigate(-1)}
            onExit={() => navigate('/')}
          />
        </div>

        <div className="flex-1 px-6 mt-8 overflow-y-auto">
          {/* Title */}
          <h1 className="text-[28px] font-bold text-purple-900 mb-2">
            {t('auth.onboarding.signupConfirm.profileTitle')}
          </h1>
          <p className="text-gray-600 text-sm mb-8">
            {t('auth.onboarding.signupConfirm.profileSubtitle')}
          </p>

          {/* Form Container */}
          <div className="signupConfirmOuterContainer">
            <form
              className="signupConfirmFormContainer"
              onSubmit={(e) => {
                e.preventDefault();
                handleRegister();
              }}
            >
            <MInputField
              label="What's your first name?"
              placeholder="Not Answered"
              value={firstName}
              autoFocus
              onChange={(e) => {
                setFirstName(e.target.value);
              }}
              onFocus={(e) => {
                setTimeout(() => {
                  e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
              disabled={loading}
            />
            <MInputField
              label="What's your last name?"
              placeholder="Not Answered"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
              }}
              onFocus={(e) => {
                setTimeout(() => {
                  e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
              disabled={loading}
            />
            <MDateInputField
              placeholder="Not Answered"
              value={dateValue}
              onDateChange={(date) =>
                setDob(
                  date
                    ? `${date.getFullYear()}-${String(
                      date.getMonth() + 1
                    ).padStart(2, "0")}`
                    : ""
                )
              }
              initialPickerValue={
                (dob && new Date(dob)) || DEFAULT_START_DATE
              }
              variant="Underlined"
              size="md"
              showLabel
              label="What's your birth month & year?"
              datePickerTitle="Select month & year"
              hideDate={true}
              formatDate={(date?: Date) => {
                return date
                  ? date.toLocaleString("en-US", {
                    month: "short",
                    year: "numeric",
                  })
                  : "";
              }}
              maxDate={maxDate}
            />
            <MInputField
              placeholder="Not answered"
              value={genderOptions.find((opt) => opt.value === gender)?.label}
              label="What is your gender?"
              readOnly
              icon={<IconCaretDownFilled />}
              showIcon
              onClick={() => setGenderDrawerOpen(true)}
              aria-label="Select gender"
            />
            <MDropDownDrawer
              open={genderDrawerOpen}
              onClose={() => setGenderDrawerOpen(false)}
              title="Select Gender"
              items={genderOptions.map((opt) => ({
                value: String(opt.value),
                label: opt.label,
              }))}
              initialValue={gender !== undefined ? String(gender) : ""}
              onSelect={(val) => {
                setGender(val ? Number(val) : undefined);
                setGenderDrawerOpen(false);
              }}
              variant="single"
            />

            {/* Continue Button */}
            <div className="pt-8 pb-8 w-full">
              <MButton
                variant="filled"
                size="lg"
                type="submit"
                className="w-full"
                disabled={
                  !firstName || !nameIsValid || !dob || !gender || loading
                }
              >
                Continue
              </MButton>
            </div>
          </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupConfirm;
